// Theme Toggle Web Component
class ThemeToggle extends HTMLElement {
  constructor() {
    super();
    this.currentTheme = this.getInitialTheme();
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.applyTheme();
  }

  getInitialTheme() {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
      return savedTheme;
    }

    // Default to auto (OS preference)
    return 'auto';
  }

  render() {
    this.innerHTML = `
      <div class="theme-toggle">
        <button class="theme-toggle-btn" id="themeToggleBtn" title="Change theme">
          <span class="theme-icon" id="themeIcon">${this.getThemeIcon()}</span>
          <span class="theme-label" id="themeLabel">${this.getThemeLabel()}</span>
        </button>
      </div>
    `;
  }

  attachEventListeners() {
    const toggleBtn = this.querySelector('#themeToggleBtn');
    toggleBtn?.addEventListener('click', () => {
      this.cycleTheme();
    });
  }

  cycleTheme() {
    const themes = ['auto', 'light', 'dark'];
    const currentIndex = themes.indexOf(this.currentTheme);
    const nextIndex = (currentIndex + 1) % themes.length;

    this.currentTheme = themes[nextIndex];
    this.saveTheme();
    this.applyTheme();
    this.updateUI();
  }

  applyTheme() {
    const html = document.documentElement;

    // Remove existing theme attributes
    html.removeAttribute('data-theme');

    if (this.currentTheme === 'light') {
      html.setAttribute('data-theme', 'light');
    } else if (this.currentTheme === 'dark') {
      html.setAttribute('data-theme', 'dark');
    }
    // For 'auto', we let CSS media queries handle it
  }

  saveTheme() {
    localStorage.setItem('theme', this.currentTheme);
  }

  updateUI() {
    const icon = this.querySelector('#themeIcon');
    const label = this.querySelector('#themeLabel');

    if (icon) icon.textContent = this.getThemeIcon();
    if (label) label.textContent = this.getThemeLabel();
  }

  getThemeIcon() {
    switch (this.currentTheme) {
    case 'light': return '☀️';
    case 'dark': return '🌙';
    case 'auto': return '🎨';
    default: return '🎨';
    }
  }

  getThemeLabel() {
    switch (this.currentTheme) {
    case 'light': return 'Light';
    case 'dark': return 'Dark';
    case 'auto': return 'Auto';
    default: return 'Auto';
    }
  }

  // Public method to get current effective theme
  getEffectiveTheme() {
    if (this.currentTheme === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return this.currentTheme;
  }

  // Public method to set theme programmatically
  setTheme(theme) {
    if (['light', 'dark', 'auto'].includes(theme)) {
      this.currentTheme = theme;
      this.saveTheme();
      this.applyTheme();
      this.updateUI();
    }
  }
}

// Register the custom element
customElements.define('theme-toggle', ThemeToggle);

// Initialize theme system
document.addEventListener('DOMContentLoaded', () => {
  // Listen for OS theme changes when in auto mode
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const themeToggle = document.querySelector('theme-toggle');
    if (themeToggle && themeToggle.currentTheme === 'auto') {
      // Re-apply auto theme to pick up OS changes
      themeToggle.applyTheme();
    }
  });
});
